import { Badge, Card, CardContent } from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { PiCheckCircle, PiWarning, PiXCircle } from 'react-icons/pi';
import { MonitorCardHeader } from './MonitorCardHeader';

interface RangeMonitorProps {
  name: string;
  description?: string;
  alarmRange?: { min: number; max: number };
  warningRange?: { min: number; max: number };
  currentValue: number;
  unit?: string;
  className?: string;
}

export function RangeMonitor({
  name,
  description,
  alarmRange,
  warningRange,
  currentValue,
  unit = '',
  className,
}: RangeMonitorProps) {
  // Determine status based on ranges
  const getStatus = () => {
    if (alarmRange) {
      if (currentValue < alarmRange.min || currentValue > alarmRange.max) {
        return { level: 'alarm', text: 'Alarm', range: alarmRange };
      }
    }

    if (warningRange) {
      if (currentValue < warningRange.min || currentValue > warningRange.max) {
        return { level: 'warning', text: 'Warning', range: warningRange };
      }
    }

    // Use alarm range as primary range for normal status, fallback to warning range
    const primaryRange = alarmRange || warningRange;
    return {
      level: 'normal',
      text: 'Normal',
      range: primaryRange,
    };
  };

  const status = getStatus();
  const activeRange = status.range;

  // Calculate percentage within the range
  const getPercentage = () => {
    if (!activeRange) return 0;

    const range = activeRange.max - activeRange.min;
    if (range <= 0) return 0;

    const valueInRange = Math.max(
      0,
      Math.min(currentValue - activeRange.min, range)
    );
    return (valueInRange / range) * 100;
  };

  const percentage = getPercentage();

  const getStatusText = () => {
    if (!activeRange) return status.text;

    if (currentValue < activeRange.min) return `Below ${status.text} Range`;
    if (currentValue > activeRange.max) return `Above ${status.text} Range`;
    return status.text;
  };

  const getStatusIcon = () => {
    switch (status.level) {
      case 'alarm':
        return <PiXCircle className="h-4 w-4 text-destructive" />;
      case 'warning':
        return <PiWarning className="h-4 w-4 text-yellow-600" />;
      default:
        return <PiCheckCircle className="h-4 w-4 text-green-600" />;
    }
  };

  const getValueColor = () => {
    switch (status.level) {
      case 'alarm':
        return 'text-destructive';
      case 'warning':
        return 'text-yellow-600';
      default:
        return 'text-foreground';
    }
  };

  const getProgressColor = () => {
    switch (status.level) {
      case 'alarm':
        return 'bg-destructive';
      case 'warning':
        return 'bg-yellow-500';
      default:
        return 'bg-green-600';
    }
  };

  const getBadgeVariant = () => {
    switch (status.level) {
      case 'alarm':
        return 'destructive';
      case 'warning':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (!activeRange) {
    return (
      <Card className={cn('w-full', className)}>
        <MonitorCardHeader
          name={name}
          description={description}
          statusIcon={<PiWarning className="h-4 w-4 text-muted-foreground" />}
        />
        <CardContent>
          <div className="text-sm text-muted-foreground">
            No range configuration available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('w-full', className)}>
      <MonitorCardHeader
        name={name}
        description={description}
        statusIcon={getStatusIcon()}
      />
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-baseline space-x-2">
            <span className={cn('text-2xl font-bold', getValueColor())}>
              {currentValue.toLocaleString()}
            </span>
            {unit && (
              <span className="text-sm text-muted-foreground ml-1">{unit}</span>
            )}
          </div>

          <div className="space-y-2">
            {/* Show specific range types instead of generic "Range" */}
            {alarmRange && warningRange ? (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Warning</span>
                  <span className="font-medium text-yellow-600">
                    {warningRange.min.toLocaleString()}
                    {unit && ` ${unit}`} - {warningRange.max.toLocaleString()}
                    {unit && ` ${unit}`}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Alarm</span>
                  <span className="font-medium text-destructive">
                    {alarmRange.min.toLocaleString()}
                    {unit && ` ${unit}`} - {alarmRange.max.toLocaleString()}
                    {unit && ` ${unit}`}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {alarmRange ? 'Alarm Range' : 'Warning Range'}
                </span>
                <span className="font-medium">
                  {activeRange.min.toLocaleString()}
                  {unit && ` ${unit}`} - {activeRange.max.toLocaleString()}
                  {unit && ` ${unit}`}
                </span>
              </div>
            )}

            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  getProgressColor()
                )}
                style={{
                  width: `${Math.min(Math.max(percentage, 0), 100)}%`,
                }}
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {percentage.toFixed(1)}% of range
              </span>
              <Badge
                variant={getBadgeVariant()}
                className={cn(
                  status.level === 'normal' &&
                    'bg-green-100 text-green-800 hover:bg-green-100',
                  status.level === 'warning' &&
                    'bg-yellow-100 text-yellow-800 hover:bg-yellow-100'
                )}
              >
                {getStatusText()}
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
